import { omit } from 'lodash'
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { ConsultationStatus } from '@/core/models/consultation.model'
import { consultationOfflineStore, OfflineConsultation } from '@/core/offline'

export function useMenuActions({
  consultation,
  setConsultation,
}: {
  consultation: OfflineConsultation | null
  setConsultation: Dispatch<SetStateAction<OfflineConsultation | null>>
}) {
  const lingui = useLingui()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const handleJoinConsultation = useCallback(() => {
    if (!consultation) return
    navigate(`/offline/consultations/${consultation._id}/local`)
  }, [consultation, navigate])

  const handleGenerateExpertise = useCallback(() => {
    // For offline mode, show placeholder message
    enqueueSnackbar(lingui._(msg`Generate expertise feature coming soon for offline consultations`), { variant: 'info' })
  }, [enqueueSnackbar, lingui])

  const handleCancelConsultation = useCallback(async () => {
    if (!consultation) return

    try {
      const updatedConsultation = await consultationOfflineStore.updateConsultation(consultation._id, { status: ConsultationStatus.Canceled })
      setConsultation(updatedConsultation)
      enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
    } catch (error) {
      console.error('Error canceling consultation:', error)
      enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
    }
  }, [consultation, enqueueSnackbar, setConsultation, t])

  return useMemo(() => {
    if (!consultation) return []
    const actions = [
      {
        label: t('page.consultation.actions.generateExpertise'),
        actionHandler: handleGenerateExpertise,
        fontColor: 'black',
        status: [ConsultationStatus.Completed],
      },
      {
        label: t('page.consultation.actions.join'),
        actionHandler: handleJoinConsultation,
        fontColor: 'black',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Active, ConsultationStatus.Scheduled],
      },
      {
        label: t('page.consultation.actions.cancel'),
        actionHandler: handleCancelConsultation,
        fontColor: 'red',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Scheduled],
      },
    ]
    return actions.filter((action) => action.status.includes(consultation.status)).map((action) => omit(action, ['status']))
  }, [consultation, t, handleCancelConsultation, handleGenerateExpertise, handleJoinConsultation])
}
